// src/components/AllSalesList.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/axios";
import toast from "react-hot-toast";
import { FaTrash, FaEdit, FaTimes, FaSearch } from "react-icons/fa";

const offerTiers = [
    { minKg: 3000, name: "Cooker / Blender / Iron" },
    { minKg: 6000, name: "Induction / Mixture Grinder / Smart Watch" },
    { minKg: 10000, name: "Luggage Bag / Blanket Set" },
    { minKg: 18000, name: "Home Theater / Kitchen Combo" },
    { minKg: 25000, name: "Android Phone" },
    { minKg: 32000, name: "Tablet / Microwave / Kitchen Gas Stove Combo" },
    { minKg: 40000, name: "Ghar Ghanti / Cooler / Tablet + Smart Watch" },
    { minKg: 50000, name: "Smart Phone / Washing Machine / RO / Premium Gift" },
    {
        minKg: 75000,
        name: "Fridge / Smartphone + Home Theater / RO + Kitchen Combo / Premium Gift",
    },
    { minKg: 100000, name: "AC / Smart TV / Smartphone / Premium Gift" },
];

export default function AllSalesList() {
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingSale, setEditingSale] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;

    const navigate = useNavigate();
    const dealer = JSON.parse(localStorage.getItem("dealer"));
    const dealerId = dealer?.id;

    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedUserHistory, setSelectedUserHistory] = useState([]);
    const [rewardTiers, setRewardTiers] = useState([]);
    const [rewardsLoading, setRewardsLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setRewardsLoading(true);

            const headers = getAuthHeaders();
            if (!headers || !dealerId) {
                toast.error("Session expired or dealer ID missing");
                navigate("/");
                return;
            }

            try {
                // Fetch sales (existing)
                const salesRes = await api.get(`/sales/dealer/${dealerId}`, { headers });
                const fetchedSales = (salesRes.data?.sales || []).sort(
                    (a, b) => new Date(b.date) - new Date(a.date)
                );
                setSales(fetchedSales);

                // Fetch rewards (adjust endpoint if needed)
                // Option A: if you have dealer/:id/reward
                const rewardsRes = await api.get(`/admin/dealers/${dealerId}/reward`, { headers });
                // Option B: if rewards are inside dealer profile, use /dealers/${dealerId}
                // const dealerRes = await api.get(`/dealers/${dealerId}`, { headers });
                // const fetchedRewards = dealerRes.data?.rewards || [];

                const fetchedRewards = rewardsRes.data?.rewards || rewardsRes.data || [];

                // Sort tiers by fromKg ascending (important!)
                const sortedTiers = [...fetchedRewards].sort((a, b) => a.fromKg - b.fromKg);

                setRewardTiers(sortedTiers);
            } catch (err) {
                console.error("Data fetch error:", err);
                const msg = err?.response?.data?.message || "Failed to load data";
                toast.error(msg);
                if (err?.response?.status === 401 || err?.response?.status === 403) {
                    navigate("/");
                }
            } finally {
                setLoading(false);
                setRewardsLoading(false);
            }
        };

        fetchData();
    }, [dealerId, navigate]);

    const getAuthHeaders = () => {
        if (!dealer?.mobile) return null;
        return { "x-auth-phone": dealer.mobile };
    };

    const getCurrentAndNextReward = (kg = 0) => {
        if (rewardTiers.length === 0) {
            return {
                current: { name: "No Reward Configured", range: "" },
                next: { name: "No Reward Configured", range: "" }
            };
        }

        let current = { name: "--", range: "" };
        let next = { name: "--", range: "" };

        for (let i = 0; i < rewardTiers.length; i++) {
            const tier = rewardTiers[i];
            const from = tier.fromKg || 0;
            const to = tier.toKg || Infinity;

            if (kg >= from && kg <= to) {
                current = {
                    name: tier.rewards?.join(" / ") || "Reward Available",
                    range: `${from.toLocaleString()} – ${to === Infinity ? "∞" : to.toLocaleString()} kg`
                };
                // Next tier (if exists)
                if (i + 1 < rewardTiers.length) {
                    const nextTier = rewardTiers[i + 1];
                    next = {
                        name: nextTier.rewards?.join(" / ") || "Next Reward",
                        range: `${nextTier.fromKg.toLocaleString()} – ${nextTier.toKg?.toLocaleString() || "∞"} kg`
                    };
                }
                return { current, next };
            }

            // If kg is before this tier → this is next
            if (kg < from) {
                next = {
                    name: tier.rewards?.join(" / ") || "Next Reward",
                    range: `${from.toLocaleString()} – ${to === Infinity ? "∞" : to.toLocaleString()} kg`
                };
                break;
            }
        }

        // If kg exceeds all tiers → current = last tier, next = max achieved
        if (kg >= rewardTiers[rewardTiers.length - 1].fromKg) {
            const lastTier = rewardTiers[rewardTiers.length - 1];
            current = {
                name: lastTier.rewards?.join(" / ") || "Max Reward Achieved",
                range: `${lastTier.fromKg.toLocaleString()} – ${lastTier.toKg?.toLocaleString() || "∞"} kg`
            };
        }

        return { current, next };
    };

    const fetchAllSales = async () => {
        setLoading(true);
        try {
            const headers = getAuthHeaders();
            if (!headers) {
                toast.error("Session expired. Please login again.");
                navigate("/");
                return;
            }
            if (!dealerId) {
                toast.error("Dealer ID not found in local storage");
                return;
            }
            const res = await api.get(`/sales/dealer/${dealerId}`, { headers });
            const fetchedSales = (res.data?.sales || []).sort(
                (a, b) => new Date(b.date) - new Date(a.date)
            );
            setSales(fetchedSales);
            setPage(1);
        } catch (err) {
            console.error("FETCH SALES ERROR:", err);
            const msg =
                err?.response?.data?.message ||
                (err?.response?.status === 401 || err?.response?.status === 403
                    ? "Unauthorized. Please login again."
                    : "Failed to load sales");
            toast.error(msg);
            if (err?.response?.status === 401 || err?.response?.status === 403) {
                navigate("/");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllSales();
    }, []);

    // Filtered Sales
    const filteredSales = useMemo(() => {
        const term = searchTerm.toLowerCase().trim();
        if (!term) return sales;

        return sales.filter((s) => {
            const nameMatch = s.userName?.toLowerCase().includes(term);
            const mobileMatch = s.userMobile?.toLowerCase().includes(term);
            const dateMatch = new Date(s.date).toLocaleDateString("en-IN").toLowerCase().includes(term);
            const qtyMatch = String(s.quantity || "").includes(term);
            return nameMatch || mobileMatch || dateMatch || qtyMatch;
        });
    }, [sales, searchTerm]);

    // Total quantity
    const totalQuantity = useMemo(() => {
        return filteredSales.reduce((sum, sale) => sum + Number(sale.quantity || 0), 0);
    }, [filteredSales]);

    // User-wise summary
    const userWiseSales = useMemo(() => {
        const map = {};
        filteredSales.forEach((s) => {
            const key = s.userMobile;
            if (!map[key]) {
                map[key] = { name: s.userName, mobile: s.userMobile, totalKg: 0, entries: 0, history: [] };
            }
            map[key].totalKg += Number(s.quantity || 0);
            map[key].entries += 1;
            map[key].history.push(s);
        });

        // Sort users by total KG descending
        return Object.values(map).sort((a, b) => b.totalKg - a.totalKg);
    }, [filteredSales]);

    // Pagination
    const totalPages = Math.max(1, Math.ceil(userWiseSales.length / rowsPerPage));

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const resetPagination = () => setPage(1);

    const pageButtons = useMemo(() => {
        const start = Math.max(1, page - 2);
        const end = Math.min(totalPages, page + 2);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }, [page, totalPages]);

    const openUserHistory = (user) => {
        setSelectedUser(user);
        setSelectedUserHistory(user.history || []);
        setHistoryModalOpen(true);
    };

    const startEdit = (sale) => {
        setEditingSale({
            _id: sale._id,
            date: new Date(sale.date).toISOString().split("T")[0],
            quantity: sale.quantity,
            remarks: sale.remarks || "",
        });
    };

    const cancelEdit = () => setEditingSale(null);

    const saveEdit = async () => {
        if (!editingSale?.date || !editingSale?.quantity || Number(editingSale.quantity) < 0.01) {
            toast.error("Valid date and quantity required");
            return;
        }

        // ── DATE RESTRICTION VALIDATION ──
        const selectedDate = new Date(editingSale.date);
        selectedDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const currentDay = today.getDate();

        // Previous month's start
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        lastMonthStart.setHours(0, 0, 0, 0);

        // Effective min date:
        //   - If today ≤ 10th → allow previous month
        //   - If today ≥ 11th → only current month
        const effectiveMin = currentDay <= 10 ? lastMonthStart : new Date(today.getFullYear(), today.getMonth(), 1);

        if (selectedDate < effectiveMin || selectedDate > today) {
            const allowedFrom = effectiveMin.toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
            });
            const allowedTo = today.toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
            });

            toast.error(
                `Invalid date! Allowed range: ${allowedFrom} to ${allowedTo}`
            );
            return;
        }

        // ── Proceed with update if date is valid ──
        const loadingToast = toast.loading("Updating sale...");
        const roundedQty = Math.round(Number(editingSale.quantity) * 100) / 100;
        const originalHistory = [...selectedUserHistory];

        // Optimistic update
        setSelectedUserHistory((prev) =>
            prev.map((sale) =>
                sale._id === editingSale._id
                    ? {
                        ...sale,
                        date: editingSale.date,
                        quantity: roundedQty,
                        remarks: editingSale.remarks?.trim() || "",
                    }
                    : sale
            )
        );

        setSales((prev) =>
            prev.map((sale) =>
                sale._id === editingSale._id
                    ? {
                        ...sale,
                        date: editingSale.date,
                        quantity: roundedQty,
                        remarks: editingSale.remarks?.trim() || "",
                    }
                    : sale
            )
        );

        try {
            const headers = getAuthHeaders();
            if (!headers) throw new Error("Session expired");

            await api.put(
                `/sales/${editingSale._id}`,
                {
                    date: editingSale.date,
                    quantity: roundedQty,
                    remarks: editingSale.remarks?.trim() || "",
                },
                { headers }
            );

            toast.success("Sale updated", { id: loadingToast });
        } catch (err) {
            // Rollback on error
            setSelectedUserHistory(originalHistory);
            toast.error(err.response?.data?.message || "Update failed", { id: loadingToast });
        } finally {
            setEditingSale(null);
        }
    };

    const handleDelete = (saleId) => {
        toast(
            (t) => (
                <div className="max-w-md">
                    <p className="font-medium mb-4">Are you sure you want to delete this sale permanently?</p>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => toast.dismiss(t.id)}
                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={async () => {
                                toast.dismiss(t.id);
                                const loadingToast = toast.loading("Deleting sale...");
                                try {
                                    const headers = getAuthHeaders();
                                    if (!headers) {
                                        toast.error("Session expired. Please login again.", { id: loadingToast });
                                        navigate("/");
                                        return;
                                    }

                                    await api.delete(`/sales/${saleId}`, { headers });

                                    setSales((prev) => prev.filter((s) => s._id !== saleId));
                                    setSelectedUserHistory((prev) => prev.filter((s) => s._id !== saleId));

                                    toast.success("Sale deleted", { id: loadingToast });
                                } catch (err) {
                                    toast.error(err.response?.data?.message || "Delete failed", { id: loadingToast });
                                }
                            }}
                            className="px-4 py-2 text-white bg-red-600 rounded hover:bg-red-700"
                        >
                            Delete
                        </button>
                    </div>
                </div>
            ),
            { duration: Infinity }
        );
    };

    const closeModal = () => {
        setHistoryModalOpen(false);
        setEditingSale(null);
    };

    const first = () => setPage(1);
    const prev = () => setPage((p) => Math.max(1, p - 1));
    const next = () => setPage((p) => Math.min(totalPages, p + 1));
    const last = () => setPage(totalPages);

    const PaginationBar = () =>
        totalPages > 1 && (
            <div className="px-4 py-3 bg-gray-50 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-xs sm:text-sm text-gray-600">
                    Showing <b>{(page - 1) * rowsPerPage + 1}</b> -{" "}
                    <b>{Math.min(page * rowsPerPage, userWiseSales.length)}</b> of{" "}
                    <b>{userWiseSales.length}</b>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={first}
                        disabled={page === 1}
                        className="h-9 px-3 rounded-lg bg-white border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-xs sm:text-sm"
                    >
                        First
                    </button>
                    <button
                        onClick={prev}
                        disabled={page === 1}
                        className="h-9 px-3 rounded-lg bg-white border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-xs sm:text-sm"
                    >
                        Prev
                    </button>
                    <div className="flex items-center gap-1">
                        {pageButtons.map((p) => (
                            <button
                                key={p}
                                onClick={() => setPage(p)}
                                className={`h-9 w-9 rounded-lg border transition font-semibold text-xs sm:text-sm ${p === page ? "bg-yellow-600 text-white border-yellow-600" : "bg-white hover:bg-gray-100"
                                    }`}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={next}
                        disabled={page === totalPages}
                        className="h-9 px-3 rounded-lg bg-white border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-xs sm:text-sm"
                    >
                        Next
                    </button>
                    <button
                        onClick={last}
                        disabled={page === totalPages}
                        className="h-9 px-3 rounded-lg bg-white border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-xs sm:text-sm"
                    >
                        Last
                    </button>
                </div>
            </div>
        );

    const today = new Date().toISOString().split("T")[0];
    const fortyDaysAgo = new Date();
    fortyDaysAgo.setDate(fortyDaysAgo.getDate() - 40);
    const minDate = fortyDaysAgo.toISOString().split("T")[0];

    return (
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border p-4 sm:p-6">
            {/* Stats Boxes */}
            <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-5">
                <div className="bg-yellow-200 border border-yellow-600 rounded-xl p-3 sm:p-4 flex flex-col items-center text-center hover:shadow-md transition">
                    <h2 className="text-base sm:text-xl font-extrabold text-gray-900">Sale Entries</h2>
                    <p className="text-lg sm:text-2xl font-bold text-yellow-700 mt-1">{userWiseSales.length}</p>
                </div>

                <div className="bg-yellow-200 border border-yellow-600 rounded-xl p-3 sm:p-4 flex flex-col items-center text-center hover:shadow-md transition">
                    <h2 className="text-base sm:text-xl font-extrabold text-gray-900">Total Sales</h2>
                    <p className="text-lg sm:text-2xl font-bold text-yellow-700 mt-1 flex items-baseline">
                        {Math.round(totalQuantity).toLocaleString()}
                        <span className="text-base sm:text-xl font-semibold ml-1">KG</span>
                    </p>
                </div>
            </div>

            {/* Search */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder="Search by name, mobile, date, qty..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            resetPagination();
                        }}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                </div>
            </div>

            {loading ? (
                <div className="p-10 sm:p-16 text-center text-gray-600 text-sm sm:text-lg">Loading sales...</div>
            ) : userWiseSales.length === 0 ? (
                <div className="p-10 sm:p-16 text-center text-gray-600 text-sm sm:text-lg">No sales found.</div>
            ) : (
                <div className="mt-4">
                    <div className="rounded-xl border bg-white overflow-hidden">
                        {/* MOBILE TABLE */}
                        <div className="sm:hidden overflow-x-auto">
                            <table className="w-full text-xs border-collapse">
                                <thead className="bg-gray-50 sticky top-0 z-10 border-b">
                                    <tr>
                                        <th className="text-left px-2 py-2 font-semibold text-gray-700 whitespace-nowrap">Customers</th>
                                        <th className="text-left px-2 py-2 font-semibold text-gray-700 whitespace-nowrap">Quantity (KG)</th>
                                        <th className="text-left px-2 py-2 font-semibold text-gray-700 whitespace-nowrap">Current</th>
                                        <th className="text-left px-2 py-2 font-semibold text-gray-700 whitespace-nowrap">Next</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {userWiseSales.slice((page - 1) * rowsPerPage, page * rowsPerPage).map((user) => {
                                        const { current, next } = getCurrentAndNextReward(user.totalKg);
                                        return (
                                            <tr
                                                key={user.mobile}
                                                onClick={() => openUserHistory(user)}
                                                className="hover:bg-yellow-50 cursor-pointer transition"
                                            >
                                                <td className="px-2 py-2.5">
                                                    <p className="font-bold text-gray-900 text-sm">{user.name}</p>
                                                    <p className="text-xs text-gray-600">{user.mobile}</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">{user.entries} Entries</p>
                                                </td>
                                                <td className="px-2 py-2.5 font-bold text-yellow-700 whitespace-nowrap text-sm">
                                                    {Math.round(user.totalKg || 0)} KG
                                                </td>
                                                <td className="px-5 py-3">
                                                    <div className="flex flex-col gap-1">
                                                        <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                                                            {current.name}
                                                        </span>
                                                        {current.range && (
                                                            <span className="text-xs text-gray-600">{current.range}</span>
                                                        )}
                                                    </div>
                                                </td>


                                                <td className="px-5 py-3">
                                                    <div className="flex flex-col gap-1">
                                                        <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">
                                                            {next.name}
                                                        </span>
                                                        {next.range && (
                                                            <span className="text-xs text-gray-600">{next.range}</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* DESKTOP TABLE */}
                        <div className="hidden sm:block overflow-auto" style={{ height: "420px" }}>
                            <table className="w-full text-sm min-w-[1100px]">
                                <thead className="bg-gray-50 sticky top-0 z-10 border-b">
                                    <tr>
                                        <th className="text-left px-5 py-3 font-semibold text-gray-700 whitespace-nowrap">Customers</th>
                                        <th className="text-left px-5 py-3 font-semibold text-gray-700 whitespace-nowrap">Quantity (KG)</th>
                                        <th className="text-left px-5 py-3 font-semibold text-gray-700 whitespace-nowrap">Current Reward</th>
                                        <th className="text-left px-5 py-3 font-semibold text-gray-700 whitespace-nowrap">Next Reward</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {userWiseSales.slice((page - 1) * rowsPerPage, page * rowsPerPage).map((user) => {
                                        const { current, next } = getCurrentAndNextReward(user.totalKg);
                                        return (
                                            <tr
                                                key={user.mobile}
                                                onClick={() => openUserHistory(user)}
                                                className="hover:bg-yellow-50 transition cursor-pointer"
                                            >
                                                <td className="px-5 py-3">
                                                    <p className="font-bold text-gray-900">{user.name}</p>
                                                    <p className="text-sm text-gray-500">{user.mobile}</p>
                                                </td>
                                                <td className="px-5 py-3 font-extrabold text-yellow-700 whitespace-nowrap">
                                                    {Math.round(user.totalKg || 0)} KG
                                                    <p className="text-xs text-gray-500 font-normal">{user.entries} Entries</p>
                                                </td>
                                                <td className="px-5 py-3">
                                                    <div className="flex flex-col gap-1">
                                                        <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                                                            {current.name}
                                                        </span>
                                                        {current.range && (
                                                            <span className="text-xs text-gray-600">{current.range}</span>
                                                        )}
                                                    </div>
                                                </td>


                                                <td className="px-5 py-3">
                                                    <div className="flex flex-col gap-1">
                                                        <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">
                                                            {next.name}
                                                        </span>
                                                        {next.range && (
                                                            <span className="text-xs text-gray-600">{next.range}</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <PaginationBar />
                    </div>
                </div>
            )}

            {/* HISTORY MODAL */}
            {historyModalOpen && selectedUser && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-6 flex flex-col max-h-[80vh] relative">
                        <button
                            onClick={closeModal}
                            className="absolute top-3 right-3 text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-full p-2"
                        >
                            <FaTimes size={16} />
                        </button>

                        <h2 className="text-xl font-extrabold mb-4">{selectedUser.name} Sales History</h2>

                        {/* Search in modal */}
                        <div className="relative mb-4">
                            <input
                                type="text"
                                placeholder="Search sales..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                            />
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        </div>

                        {/* Date restriction logic for edit */}
                        {(() => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const currentDay = today.getDate();

                            const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                            lastMonthStart.setHours(0, 0, 0, 0);

                            const effectiveMinDate = currentDay <= 10 ? lastMonthStart : new Date(today.getFullYear(), today.getMonth(), 1);
                            const minDateStr = effectiveMinDate.toISOString().split("T")[0];
                            const maxDateStr = today.toISOString().split("T")[0];

                            const currentMonthName = today.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
                            const lastMonthName = lastMonthStart.toLocaleDateString("en-IN", { month: "long", year: "numeric" });

                            return (
                                <div className="flex-1 overflow-y-auto space-y-3">
                                    {(() => {
                                        const term = searchTerm.toLowerCase().trim();
                                        const filtered = term
                                            ? selectedUserHistory.filter((h) => {
                                                return (
                                                    String(h.quantity || "").includes(term) ||
                                                    new Date(h.date).toLocaleDateString("en-IN").toLowerCase().includes(term) ||
                                                    (h.remarks || "").toLowerCase().includes(term)
                                                );
                                            })
                                            : selectedUserHistory;

                                        return filtered.length === 0 ? (
                                            <p className="text-gray-500 text-center py-10">
                                                {term ? "No matching sales found" : "No sales data available"}
                                            </p>
                                        ) : (
                                            filtered.map((sale) => (
                                                <div key={sale._id} className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <div>
                                                            <p className="text-sm text-gray-500">
                                                                Date: {new Date(sale.date).toLocaleDateString("en-IN")}
                                                            </p>
                                                            <p className="font-extrabold text-yellow-700 text-lg">{sale.quantity} KG</p>
                                                            {sale.remarks && (
                                                                <p className="text-sm text-gray-600 mt-1">Remarks: {sale.remarks}</p>
                                                            )}
                                                        </div>

                                                        <div className="flex gap-3">
                                                            <button
                                                                onClick={() => startEdit(sale)}
                                                                className="text-blue-600 hover:text-blue-800 transition"
                                                                title="Edit Sale"
                                                            >
                                                                <FaEdit size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(sale._id)}
                                                                className="text-red-600 hover:text-red-800 transition"
                                                                title="Delete Sale"
                                                            >
                                                                <FaTrash size={18} />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Edit Form with Date Restriction */}
                                                    {editingSale && editingSale._id === sale._id && (
                                                        <div className="mt-4 p-3 bg-white border border-gray-200 rounded-lg">
                                                            <div className="space-y-3">
                                                                <div>
                                                                    <label className="block text-xs text-gray-600 mb-1">
                                                                        Date <span className="text-red-600">*</span>
                                                                    </label>
                                                                    <input
                                                                        type="date"
                                                                        value={editingSale.date.split("T")[0]} // remove time part
                                                                        onChange={(e) =>
                                                                            setEditingSale({ ...editingSale, date: e.target.value })
                                                                        }
                                                                        min={minDateStr}
                                                                        max={maxDateStr}
                                                                        className="w-full border rounded px-2 py-1.5 text-sm"
                                                                    />
                                                                    <p className="text-xs text-gray-500 mt-1">
                                                                        Allowed: {currentDay <= 10 ? `${lastMonthName} aur ` : ""}
                                                                        {currentMonthName} tak
                                                                    </p>
                                                                </div>

                                                                <div>
                                                                    <label className="block text-xs text-gray-600 mb-1">
                                                                        Quantity (KG) <span className="text-red-600">*</span>
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0.01"
                                                                        value={editingSale.quantity}
                                                                        onChange={(e) => {
                                                                            let val = e.target.value;
                                                                            if (val.includes(".")) {
                                                                                const [intPart, decPart] = val.split(".");
                                                                                val = intPart + "." + decPart.slice(0, 2);
                                                                            }
                                                                            setEditingSale({ ...editingSale, quantity: val });
                                                                        }}
                                                                        className="w-full border rounded px-2 py-1.5 text-sm"
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <label className="block text-xs text-gray-600 mb-1">Remarks</label>
                                                                    <textarea
                                                                        value={editingSale.remarks}
                                                                        onChange={(e) =>
                                                                            setEditingSale({ ...editingSale, remarks: e.target.value })
                                                                        }
                                                                        className="w-full border rounded px-2 py-1.5 text-sm min-h-[60px]"
                                                                        placeholder="TMT/Structure or any other remarks"
                                                                    />
                                                                </div>

                                                                <div className="flex gap-3 justify-end mt-3">
                                                                    <button
                                                                        onClick={cancelEdit}
                                                                        className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                    <button
                                                                        onClick={saveEdit}
                                                                        className="px-4 py-2 text-sm text-white bg-yellow-600 rounded hover:bg-yellow-700"
                                                                    >
                                                                        Save
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        );
                                    })()}
                                </div>
                            );
                        })()}

                        {/* Summary Footer */}
                        <div className="bg-yellow-100 border-t border-yellow-300 rounded-xl p-4 mt-4">
                            <p className="font-extrabold">
                                Total Sales:{" "}
                                {selectedUserHistory
                                    .reduce((a, b) => a + Number(b.quantity || 0), 0)
                                    .toLocaleString()}{" "}
                                KG
                            </p>
                            <p className="font-semibold text-gray-700">Total Entries: {selectedUserHistory.length}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}